import React, { useEffect, useRef, useState } from "react";
import { FitAddon } from "@xterm/addon-fit";
import { Terminal } from "@xterm/xterm";
import Swal from "sweetalert2";
import "@xterm/xterm/css/xterm.css";

import {
    closeDeploymentTerminal,
    closeSiteTerminal,
    closeLocalTerminal,
    DeploymentTransferCredentials,
    openDeploymentTerminal,
    openLocalTerminal,
    openSiteTerminal,
    readDeploymentTerminal,
    readLocalTerminal,
    readSiteTerminal,
    resizeDeploymentTerminal,
    resizeLocalTerminal,
    resizeSiteTerminal,
    writeDeploymentTerminal,
    writeLocalTerminal,
    writeSiteTerminal,
} from "../../api/deployments";
import { Site } from "../../api/sites";

type Props = {
    kind?: "ssh" | "local";
    historyId?: string;
    site?: Site;
    credentials?: DeploymentTransferCredentials;
    workingDirectory: string;
    active: boolean;
    siteTerminalId?: number;
};

const terminalErrorMessage = (error: unknown): string => {
    const responseData = (error as {
        response?: { data?: { message?: string; detail?: string } };
    })?.response?.data;
    return (
        responseData?.message ||
        responseData?.detail ||
        "터미널을 열지 못했습니다. 연결 정보와 실행 환경을 확인해 주세요."
    );
};

const SshTerminalPane: React.FC<Props> = ({
    kind = "ssh",
    historyId,
    site,
    credentials,
    workingDirectory,
    active,
    siteTerminalId,
}) => {
    const [isConnecting, setIsConnecting] = useState(true);
    const [isConnected, setIsConnected] = useState(false);
    const sessionIdRef = useRef<string | null>(null);
    const pollingRef = useRef(false);
    const terminalContainerRef = useRef<HTMLDivElement>(null);
    const terminalRef = useRef<Terminal | null>(null);
    const fitAddonRef = useRef<FitAddon | null>(null);
    const writeInputRef = useRef<(input: string) => void>(() => undefined);
    const activeRef = useRef(active);
    const isLocal = kind === "local";
    const host = credentials?.sshHost ?? site?.sshHost ?? "";
    const port = credentials?.sshPort ?? site?.sshPort ?? 22;
    const username = credentials?.sshUsername ?? site?.sshUsername ?? "";

    useEffect(() => {
        const container = terminalContainerRef.current;
        if (!container) return;

        let disposed = false;
        let pollTimer: number | undefined;
        let inputFlushTimer: number | undefined;
        let resizeTimer: number | undefined;
        let pendingInput = "";
        let writeQueue = Promise.resolve();
        const openTerminalSession = () => {
            if (isLocal) return openLocalTerminal(workingDirectory);
            if (!historyId || !credentials) {
                return Promise.reject(new Error("SSH 터미널 연결 정보가 없습니다."));
            }
            return siteTerminalId == null
                ? openDeploymentTerminal(historyId, credentials)
                : openSiteTerminal(siteTerminalId, credentials);
        };
        const writeTerminalInput = (sessionId: string, input: string) =>
            isLocal
                ? writeLocalTerminal(sessionId, input)
                : siteTerminalId == null && historyId
                ? writeDeploymentTerminal(historyId, sessionId, input)
                : writeSiteTerminal(siteTerminalId!, sessionId, input);
        const readTerminalOutput = (sessionId: string) =>
            isLocal
                ? readLocalTerminal(sessionId)
                : siteTerminalId == null && historyId
                ? readDeploymentTerminal(historyId, sessionId)
                : readSiteTerminal(siteTerminalId!, sessionId);
        const resizeTerminal = (sessionId: string, cols: number, rows: number) =>
            isLocal
                ? resizeLocalTerminal(sessionId, cols, rows)
                : siteTerminalId == null && historyId
                ? resizeDeploymentTerminal(historyId, sessionId, cols, rows)
                : resizeSiteTerminal(siteTerminalId!, sessionId, cols, rows);
        const closeTerminal = (sessionId: string) =>
            isLocal
                ? closeLocalTerminal(sessionId)
                : siteTerminalId == null && historyId
                ? closeDeploymentTerminal(historyId, sessionId)
                : closeSiteTerminal(siteTerminalId!, sessionId);

        const terminal = new Terminal({
            allowTransparency: true,
            convertEol: false,
            customGlyphs: true,
            cursorBlink: true,
            cursorStyle: "block",
            fontFamily:
                '"D2Coding", "D2CodingLigature Nerd Font Mono", "JetBrainsMonoNL NF", "GulimChe", "MS Gothic", Consolas, monospace',
            fontSize: 13,
            letterSpacing: 0,
            lineHeight: 1.25,
            scrollback: 5_000,
            theme: {
                background: "#07101c",
                foreground: "#dbe4f0",
                cursor: "#f8fafc",
                cursorAccent: "#07101c",
                selectionBackground: "rgba(96, 165, 250, 0.32)",
                black: "#111827",
                red: "#f87171",
                green: "#4ade80",
                yellow: "#facc15",
                blue: "#60a5fa",
                magenta: "#e879f9",
                cyan: "#22d3ee",
                white: "#e5e7eb",
                brightBlack: "#64748b",
                brightRed: "#fca5a5",
                brightGreen: "#86efac",
                brightYellow: "#fde047",
                brightBlue: "#93c5fd",
                brightMagenta: "#f0abfc",
                brightCyan: "#67e8f9",
                brightWhite: "#ffffff",
            },
        });
        const fitAddon = new FitAddon();
        terminal.loadAddon(fitAddon);
        terminal.open(container);
        terminalRef.current = terminal;
        fitAddonRef.current = fitAddon;

        const reportError = (error: unknown) => {
            if (disposed) return;
            terminal.writeln(`\r\n\u001b[31m${terminalErrorMessage(error)}\u001b[0m`);
            setIsConnected(false);
        };

        const enqueueInput = (input: string) => {
            const sessionId = sessionIdRef.current;
            if (!sessionId || !input || disposed) return;

            writeQueue = writeQueue
                .then(() => writeTerminalInput(sessionId, input))
                .catch(reportError);
        };

        const flushInput = () => {
            if (inputFlushTimer) {
                window.clearTimeout(inputFlushTimer);
                inputFlushTimer = undefined;
            }
            const sessionId = sessionIdRef.current;
            const input = pendingInput;
            pendingInput = "";
            if (!sessionId || !input || disposed) return;

            enqueueInput(input);
        };

        writeInputRef.current = (input: string) => {
            flushInput();
            enqueueInput(input);
            terminal.focus();
        };

        const dataDisposable = terminal.onData((data) => {
            if (!sessionIdRef.current || disposed) return;
            pendingInput += data;
            if (
                data.includes("\r") ||
                data.includes("\u0003") ||
                pendingInput.length >= 128
            ) {
                flushInput();
            } else if (!inputFlushTimer) {
                inputFlushTimer = window.setTimeout(flushInput, 24);
            }
        });

        const resizeDisposable = terminal.onResize(({ cols, rows }) => {
            const sessionId = sessionIdRef.current;
            if (!sessionId || disposed) return;
            if (resizeTimer) window.clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                void resizeTerminal(sessionId, cols, rows).catch(reportError);
            }, 100);
        });

        const fitTerminal = () => {
            if (disposed || container.clientWidth === 0 || container.clientHeight === 0) return;
            runCatchingFit(fitAddon);
        };
        const resizeObserver = new ResizeObserver(fitTerminal);
        resizeObserver.observe(container);
        window.setTimeout(fitTerminal, 0);

        const pollOutput = async (): Promise<boolean> => {
            const sessionId = sessionIdRef.current;
            if (!sessionId || disposed) return false;
            if (pollingRef.current) return true;
            pollingRef.current = true;
            try {
                const result = await readTerminalOutput(sessionId);
                if (disposed) return false;
                if (result.output) terminal.write(result.output);
                setIsConnected(result.connected);
                if (!result.connected) {
                    terminal.writeln(
                        `\r\n\u001b[90m${isLocal ? "로컬" : "SSH"} 터미널 연결이 종료되었습니다.\u001b[0m`
                    );
                    return false;
                }
                return true;
            } catch (error) {
                reportError(error);
                return false;
            } finally {
                pollingRef.current = false;
            }
        };

        const schedulePoll = () => {
            if (disposed) return;
            pollTimer = window.setTimeout(async () => {
                const shouldContinue = await pollOutput();
                if (shouldContinue) schedulePoll();
            }, activeRef.current ? 100 : 750);
        };

        const openTerminal = async () => {
            try {
                const session = await openTerminalSession();
                if (disposed) {
                    void closeTerminal(session.sessionId);
                    return;
                }
                sessionIdRef.current = session.sessionId;
                setIsConnecting(false);
                setIsConnected(true);
                fitTerminal();
                await resizeTerminal(session.sessionId, terminal.cols, terminal.rows);
                if (await pollOutput()) schedulePoll();
                terminal.focus();
            } catch (error) {
                setIsConnecting(false);
                reportError(error);
            }
        };

        void openTerminal();
        return () => {
            disposed = true;
            if (pollTimer) window.clearTimeout(pollTimer);
            if (inputFlushTimer) window.clearTimeout(inputFlushTimer);
            if (resizeTimer) window.clearTimeout(resizeTimer);
            resizeObserver.disconnect();
            dataDisposable.dispose();
            resizeDisposable.dispose();
            terminal.dispose();
            terminalRef.current = null;
            fitAddonRef.current = null;
            writeInputRef.current = () => undefined;
            const sessionId = sessionIdRef.current;
            sessionIdRef.current = null;
            if (sessionId) void closeTerminal(sessionId);
        };
    }, [credentials, historyId, isLocal, siteTerminalId, workingDirectory]);

    useEffect(() => {
        activeRef.current = active;
        if (!active) return;
        const frame = window.requestAnimationFrame(() => {
            const fitAddon = fitAddonRef.current;
            if (fitAddon) runCatchingFit(fitAddon);
            terminalRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(frame);
    }, [active]);

    const sendControlC = () => {
        if (sessionIdRef.current && isConnected) writeInputRef.current("\u0003");
    };

    const runPatchScript = async () => {
        if (!sessionIdRef.current || !isConnected) return;

        const result = await Swal.fire({
            icon: "question",
            title: "patch.sh 실행",
            text: `${workingDirectory}에서 bash patch.sh를 실행할까요?`,
            showCancelButton: true,
            confirmButtonText: "실행",
            cancelButtonText: "취소",
            reverseButtons: true,
        });
        if (result.isConfirmed) writeInputRef.current("bash patch.sh\r");
    };

    return (
        <div className="dp-ssh-terminal-pane">
            <div className="dp-ssh-console-location">
                <div className="dp-ssh-console-context">
                    <span
                        className={`dp-ssh-console-status${isConnected ? "" : " is-offline"}`}
                        aria-hidden="true"
                    />
                    <strong>{isLocal ? "로컬 PowerShell" : `${username}@${host}:${port}`}</strong>
                    <span>{isLocal ? "로컬 디렉터리" : "작업 디렉터리"}</span>
                    <code>{workingDirectory}</code>
                </div>
                <div className="dp-ssh-console-tools">
                    {!isLocal && (
                        <button
                            type="button"
                            className="dp-ssh-console-preset"
                            aria-label="bash patch.sh 실행"
                            disabled={!isConnected}
                            onClick={runPatchScript}
                        >
                            patch.sh 실행
                        </button>
                    )}
                    <button type="button" disabled={!isConnected} onClick={sendControlC}>
                        중단
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            terminalRef.current?.clear();
                            terminalRef.current?.focus();
                        }}
                    >
                        화면 지우기
                    </button>
                </div>
            </div>

            <div
                className="dp-ssh-xterm-shell"
                onMouseDown={() => terminalRef.current?.focus()}
            >
                {isConnecting && (
                    <div className="dp-ssh-console-running dp-ssh-xterm-loading">
                        <span />
                        {isLocal ? "로컬 터미널 실행 중" : "SSH 터미널 연결 중"}
                    </div>
                )}
                <div
                    ref={terminalContainerRef}
                    className="dp-ssh-xterm-container"
                    aria-label={`${isLocal ? "로컬" : "SSH"} 터미널 입력 및 출력`}
                />
            </div>
        </div>
    );
};

const runCatchingFit = (fitAddon: FitAddon) => {
    try {
        fitAddon.fit();
    } catch {
        // The container can briefly be unmeasurable while the modal is closing.
    }
};

export default SshTerminalPane;
